import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { translations } from "../i18n/translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "es");
  const [token, setToken] = useState(() => localStorage.getItem("token") || null);
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const t = translations[lang];

  useEffect(() => {
    localStorage.setItem("lang", lang);
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const authHeaders = useCallback(() => (
    token ? { Authorization: `Bearer ${token}` } : {}
  ), [token]);

  const axiosOpts = useCallback(() => ({
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  }), [token]);

  const fetchMe = useCallback(async () => {
    // If returning from Google OAuth, let AuthCallback handle it
    if (window.location.hash?.includes("session_id=")) {
      setChecking(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setUser(data);
    } catch {
      setUser(null);
      if (token) {
        setToken(null);
        localStorage.removeItem("token");
      }
    } finally {
      setChecking(false);
    }
  }, [token]);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    localStorage.setItem("token", data.token);
    setToken(data.token);
    setUser({ id: data.id, email: data.email, name: data.name });
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
    localStorage.setItem("token", data.token);
    setToken(data.token);
    setUser({ id: data.id, email: data.email, name: data.name });
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} });
    } catch { /* ignore */ }
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const toggleLang = () => setLang((l) => (l === "es" ? "en" : "es"));

  return (
    <AppContext.Provider value={{ lang, setLang, toggleLang, t, user, setUser, token, checking, login, register, logout, authHeaders, axiosOpts }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
